/**
 * ChatUtils
 * Utility functions for chat operations
 * Matches Flutter implementation: lib/utils/chat_utils.dart
 */

import { env } from '../../config/env';
import type { ChatMessageDto } from '../../data/dtos/chat/chatMessageDto';
import type { ChatMessageEntity } from '../entities/chat/chatMessageEntity';
import type { UserEntity } from '../entities/user/userEntity';
import { BlockedOverlayVariant } from '../enums/chat/blockedOverlayVariant';
import { ReadReceiptMode } from '../enums/chat/readReceiptMode';
import { ChatMessageType } from '../enums/chatMessageType';

/**
 * Resolved CSS corner radii for a chat bubble in `px` units. Consumed by
 * per-type bubble subcomponents — they spread these values into their
 * outer container `style`.
 */
export interface BubbleCornerRadii {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
}

interface BubbleCornerRadiiInput {
  /** Whether the message is owned by the current user (right-aligned). */
  isOwn: boolean;
  /**
   * Whether this is the LAST message of a same-sender run. Flutter's
   * `isLastMessageRight` (own) / `isLastMessageLeft` (peer). True →
   * the run-end corner collapses to `runEndRadius`; other three stay
   * at `runMidRadius`.
   */
  isLastOfRun: boolean;
  /**
   * Radius (in px) for the three corners that stay at the "mid" value.
   * Maps to Flutter's 14.0 — callers typically pass
   * `responsive.borderRadius.lg`.
   */
  runMidRadius: number;
  /**
   * Radius (in px) for the run-end corner. Maps to Flutter's 4.0 —
   * callers typically pass `responsive.borderRadius.sm`.
   */
  runEndRadius: number;
}

export class ChatUtils {
  /**
   * Get chat message type for share operations
   * Returns shareUser, sharePhoto, or shareVideo based on mediaInfo
   */
  static getShareMessageType(mediaInfo?: { type: number }): number {
    if (!mediaInfo) {
      return ChatMessageType.shareUser;
    }

    return mediaInfo.type === 0
      ? ChatMessageType.sharePhoto
      : ChatMessageType.shareVideo;
  }

  /**
   * Whether a chat message's `content` should be routed through E2EE
   * decryption before the chat room renders it.
   *
   * Returns `true` only for non-null, non-empty `content` on one of
   * `text` / `shareUser` / `sharePhoto` / `shareVideo`.
   *
   * Shared by BOTH chat-room read paths — `FetchChatMessagesUseCase`
   * (paginated fetch / reload) and `SubscribeToChatUpdatesUseCase` (live
   * listener). They must call the identical predicate: a divergence would let
   * a shared caption decrypt on reload but not when it arrives live.
   *
   * `type` is taken as a raw `number` because that is the DTO field's type
   * (`chatMessageDto.ts:19`); it is compared against `ChatMessageType` members.
   *
   * Three facts this gate encodes:
   *
   * (a) Flutter applies NO type gate at all — `chat_screen.dart:467-469`
   *     (paginated) and `:533-537` (live listener) decrypt `content` for every
   *     message type whose `content` is non-null. The web keeps a narrower
   *     gate purely to avoid a pointless `crypto.subtle.decrypt` rejection
   *     (and the `appLogger.error` it triggers) on `gift` / `image` / `video` /
   *     `videoCall` / `reply` bodies that were never ciphertext.
   *
   * (b) The three share types are in-list because BOTH clients encrypt the
   *     share caption per peer — web `shareWithFriendsUseCase.ts:139-144`,
   *     Flutter `ShareRepository.saveBatchChatMessages`. The caption rides in
   *     ordinary `content` (`search_users_bottom_sheet.dart:377`) and Flutter
   *     renders it as ordinary decrypted message content
   *     (`chat_screen.dart:2039, 2073`).
   *
   * (c) Accepted consequence: a LEGACY Flutter-origin caption already written
   *     in plaintext now fails decryption and renders "Could not decrypt
   *     message", plus one `decrypt failed` error log per chat-room load.
   *     That is a deliberate, user-ratified pre-release outcome. The ruling
   *     is restated here in full so it stands on its own: ANYTHING `decrypt`
   *     does not successfully decrypt shows "Could not decrypt message",
   *     plaintext included — and the plaintext captions in question were
   *     written only by pre-release builds, so no released user is affected.
   *     Therefore do NOT "repair" this: no ciphertext-shape
   *     (`looksLikeCiphertext`) predicate, no plaintext pass-through branch —
   *     not here, not in `TextEncryptionService.decrypt`, not in Flutter's
   *     `E2EEImpl.decrypt` — and no log suppression to hide the error.
   *     Audit trail, committed on this branch: the `## Context` blockquote
   *     "Decision … strict decrypt failure copy, on both clients" in
   *     `workflow/story_plans/fix_chat_message_encryption_parity_story_plan.md`.
   *
   * Named `shouldDecryptMessageContent` — NOT `shouldDecryptContent` — so it
   * is never confused with its sibling `shouldDecryptNodeContent` (directly
   * below), the inbox node gate, which deliberately stays `text`-only because
   * share rows render a static label and never read `lastContent`. The two
   * predicates are filed adjacently so that divergence has one owner: any
   * change to this type list must be weighed against the node gate's list.
   */
  static shouldDecryptMessageContent(
    type: number,
    content: string | null | undefined,
  ): boolean {
    if (content == null || content === '') return false;
    return (
      type === ChatMessageType.text ||
      type === ChatMessageType.shareUser ||
      type === ChatMessageType.sharePhoto ||
      type === ChatMessageType.shareVideo
    );
  }

  /**
   * Whether a chat NODE's `lastContent` should be routed through E2EE
   * decryption before the inbox direct-messages list renders it.
   *
   * Returns `true` only for non-null, non-empty `lastContent` on a `text`-type
   * node.
   *
   * Shared by BOTH inbox read paths — `FetchChatNodesUseCase` (paginated fetch)
   * and `SubscribeToChatNodesUseCase` (live listener). They must call the
   * identical predicate: a divergence would let a row's preview decrypt on the
   * first page but not when the same conversation ticks live.
   *
   * `type` is taken as a raw `number` for the same reason as
   * `shouldDecryptMessageContent` — it is the DTO/entity field's type — and is
   * compared against `ChatMessageType` members.
   *
   * Two facts this gate encodes:
   *
   * (a) Flutter gates on `text` and nothing else — `messages_screen.dart:294`
   *     (`_getChatItems`, paginated) and `:506` (`_subscribeToChatUpdates`,
   *     live). The web adds only the non-null / non-empty check on top:
   *     Flutter feeding an empty string to `e2ee.decrypt` is a no-op there,
   *     whereas on web it makes `crypto.subtle.decrypt` throw and emits a
   *     spurious `appLogger.error`. Every non-`text` type (gift, image, video,
   *     videoCall, reply, share*) stores plaintext/structured content that
   *     `TextEncryptionService.encrypt` never produced.
   *
   * (b) The three SHARE types are intentionally EXCLUDED here — deliberately
   *     unlike `shouldDecryptMessageContent` above, which does decrypt them.
   *     This asymmetry is by design, not an omission: the chat room renders a
   *     share message's caption as ordinary decrypted content, but the inbox
   *     row renders a STATIC label for a share row and never reads
   *     `lastContent` (`getChatPreviewText.ts:48-59`, mirroring Flutter
   *     `messages_chat_item.dart:64-82`). The caption ciphertext both clients
   *     now write into `last_content` is therefore never displayed by the
   *     inbox, and decrypting it would be dead work (one peer-user fetch + one
   *     key derivation + one decrypt per share row, per page). Do NOT "fix"
   *     this by widening the list to match the message gate — widen it only if
   *     the inbox row ever starts rendering `lastContent` for share types.
   */
  static shouldDecryptNodeContent(
    type: number,
    lastContent: string | null | undefined,
  ): boolean {
    if (lastContent == null || lastContent === '') return false;
    return type === ChatMessageType.text;
  }

  /**
   * Generate group chat ID from two user IDs
   * Sorts alphabetically and joins with underscore
   * Ensures consistent chat ID regardless of user order
   */
  static getGroupChatId(userId1: string, userId2: string): string {
    return userId1 < userId2 ? `${userId1}-${userId2}` : `${userId2}-${userId1}`;
  }

  /**
   * Generate the Firebase Storage group-chat segment from two user IDs.
   * Used to build the chat upload path `chat/{storageGroupChatId}/{fileName}`
   * (Flutter `app_storage_service.dart:188-189`).
   *
   * Flutter (`chat_utils.dart:15-24`) orders the two ids by `hashCode <=` and
   * joins them with `/`. The existing web `getGroupChatId` (chatUtils.ts:71)
   * already diverges from Flutter by using lexicographic `<` ordering instead
   * of `hashCode`; we deliberately match that same lexicographic ordering here
   * so the storage id and the node id agree on which user comes first
   * (web-internal consistency). Only the separator differs from getGroupChatId
   * (`/` here vs `-` for the node id).
   */
  static getStorageGroupChatId(userId1: string, userId2: string): string {
    return userId1 < userId2 ? `${userId1}/${userId2}` : `${userId2}/${userId1}`;
  }

  /**
   * Get full server path for chat message media
   * Matches Flutter getter: ChatMessage.serverPath
   *
   * Accepts the minimal structural shape it actually reads
   * (`relativeServerPath`), so both `ChatMessageDto` and the domain
   * `ChatMessageEntity` (which carry an identical `relativeServerPath?: string`
   * field) can call it without duplicating the concatenation.
   */
  static getChatMessageServerPath(message: {
    relativeServerPath?: string;
  }): string | null {
    if (!message.relativeServerPath) {
      return null;
    }
    return env.DEFAULT_BUCKET_PREFIX + message.relativeServerPath;
  }

  /**
   * Get full thumbnail URL for chat message media
   * Matches Flutter getter: ChatMessage.thumbnail
   */
  static getChatMessageThumbnail(message: ChatMessageDto): string | null {
    if (!message.relativeThumbnailPath) {
      return null;
    }
    return env.DEFAULT_BUCKET_PREFIX + message.relativeThumbnailPath;
  }

  /**
   * Pick the first name out of a user's display name. Mirrors Flutter's
   * `peerUser.firstName` access used by the empty-state + unread copy at
   * `chat_screen.dart:1018-1040, 3412-3427`. The `UserEntity` already carries
   * a `firstName` field — we prefer that when the peer is resolved and fall
   * back to splitting `peerName` on whitespace for the not-yet-resolved
   * deep-link path.
   */
  static resolvePeerFirstName(
    peerUser: UserEntity | null,
    peerNameFallback: string,
  ): string {
    if (peerUser && peerUser.firstName && peerUser.firstName.length > 0) {
      return peerUser.firstName;
    }
    const trimmed = peerNameFallback.trim();
    if (trimmed.length === 0) return '';
    const firstSpace = trimmed.indexOf(' ');
    return firstSpace === -1 ? trimmed : trimmed.slice(0, firstSpace);
  }

  /**
   * Whether the PEER has the conversation with the current user blocked, i.e.
   * the peer has the current user in *their* `blockedConversations` list.
   *
   * Mirrors the `_peerUserConversationBlocked` term computed in Flutter at
   * `chat_screen.dart:378-381`:
   *   `_peerUser!.blockedConversations != null &&
   *    _peerUser!.blockedConversations!.contains(_currentUser!.firebaseId)`.
   *
   * Note the direction: `setBlockConversation`
   * (`users_data_source.dart:340-362`) writes `blockedConversations` on the
   * *caller's own* user doc, so when the peer blocks the conversation it is the
   * peer's `blockedConversations` array that gains the current user's firebase
   * id. The current user reads that array off the already-fetched `peerUser` —
   * no extra Firestore read or listener is required.
   *
   * Pure predicate, no UI / storage concerns. Returns `false` when the peer is
   * not yet resolved or its `blockedConversations` is absent.
   */
  static peerUserConversationBlocked(
    peerUser: UserEntity | null,
    currentUserFirebaseId: string,
  ): boolean {
    return (
      peerUser != null &&
      (peerUser.blockedConversations?.includes(currentUserFirebaseId) ?? false)
    );
  }

  /**
   * Decide which (if any) full-screen blocked overlay to render. Mirrors the
   * priority order at `chat_screen.dart:1005-1016`: current-user-blocked beats
   * current-user-blocked-by-peer beats peer-user-blocked. At most one renders
   * at a time.
   */
  static pickBlockedVariant(
    currentUserBlocked: boolean,
    currentUserBlockedByPeer: boolean,
    peerUserBlocked: boolean,
  ): BlockedOverlayVariant | null {
    if (currentUserBlocked) return BlockedOverlayVariant.currentUserBlocked;
    if (currentUserBlockedByPeer) return BlockedOverlayVariant.currentUserBlockedByPeer;
    if (peerUserBlocked) return BlockedOverlayVariant.peerUserBlocked;
    return null;
  }

  /**
   * Comparator that sorts chat messages by timestamp in descending order
   * (newest first / lower index = newer). Mirrors Flutter
   * `b.timestamp.compareTo(a.timestamp)` exactly
   * (`chat_screen.dart:345, 492, 549, 569, 587, 633`).
   */
  static sortByTimestampDesc(
    a: ChatMessageEntity,
    b: ChatMessageEntity,
  ): number {
    return b.timestamp - a.timestamp;
  }

  /**
   * Flutter `isLastMessageRight` — `chat_screen.dart:772-781`. Returns true
   * for the LAST own message of a same-sender run (the bubble whose bottom-
   * right corner collapses).
   *
   *   - True when `index === 0` (most-recent message, the list is reversed).
   *   - True when the previous list item is NOT from the current user.
   *   - False otherwise.
   *
   * `allMessages` is the same reversed list the page renders (index 0 =
   * newest). The caller is expected to look up `idFrom` on each entry.
   */
  static isLastMessageRight(
    index: number,
    allMessages: ChatMessageEntity[],
    currentUserId: string,
  ): boolean {
    if (index === 0) return true;
    if (index < 0 || index >= allMessages.length) return false;
    const prev = allMessages[index - 1];
    if (!prev) return false;
    return prev.idFrom !== currentUserId;
  }

  /**
   * Flutter `isLastMessageLeft` — `chat_screen.dart:759-770`. Returns true
   * for the LAST peer message of a same-sender run.
   *
   *   - True when `index === 0`.
   *   - True when the previous list item IS from the current user AND the
   *     current item is NOT uploading (`isUploading == null || == false`).
   *   - False otherwise.
   */
  static isLastMessageLeft(
    index: number,
    allMessages: ChatMessageEntity[],
    currentUserId: string,
  ): boolean {
    if (index === 0) return true;
    if (index < 0 || index >= allMessages.length) return false;
    const prev = allMessages[index - 1];
    const current = allMessages[index];
    if (!prev || !current) return false;
    const currentNotUploading =
      current.isUploading === undefined ||
      current.isUploading === null ||
      current.isUploading === false;
    return prev.idFrom === currentUserId && currentNotUploading;
  }

  /**
   * Pick the right `ReadReceiptMode` for an own bubble at `index` in the
   * reversed (newest-first) list. Mirrors Flutter `currentUserMessageStatus`
   * exactly (`chat_screen.dart:2284-2325`).
   *
   * Callers (`ChatMessagesList.renderItem`) call this ONCE per message so the
   * dispatcher does not have to keep `index` + `myLastReadPosition` around per
   * bubble. The return value is a primitive enum — drops the previous "leak
   * the whole `allMessages` array into every leaf" pattern.
   *
   * Peer-message bubbles never render a read receipt; this helper is only
   * meaningful when called for own bubbles.
   */
  static getReadReceiptMode(
    message: ChatMessageEntity,
    index: number,
    myLastReadPosition: number,
  ): ReadReceiptMode {
    // Failed-send branch (`chat_screen.dart:2287-2289`).
    if (message.success === false) return ReadReceiptMode.failed;
    // No own message seen yet, or this bubble sits above the last read one
    // (`chat_screen.dart:2291-2297`).
    if (myLastReadPosition === -1 || myLastReadPosition > index) {
      return ReadReceiptMode.notYetSeen;
    }
    // This bubble IS the most-recent own message the peer has read
    // (`chat_screen.dart:2299-2321`).
    if (myLastReadPosition === index) return ReadReceiptMode.lastReadAvatar;
    // Bubble is below the last-read mark — empty spacer
    // (`chat_screen.dart:2324`).
    return ReadReceiptMode.spacer;
  }

  /**
   * Pure helper. Returns the four CSS corner radii for a chat bubble per the
   * Flutter asymmetric-corners rule.
   *
   * Own bubble (`chat_screen.dart:1453-1461`):
   *   - topLeft, topRight, bottomLeft = `runMidRadius` (Flutter 14.0)
   *   - bottomRight = `isLastOfRun ? runEndRadius : runMidRadius`
   *
   * Peer bubble (`chat_screen.dart:2560-2568`):
   *   - topLeft, topRight, bottomRight = `runMidRadius` (Flutter 14.0)
   *   - bottomLeft = `isLastOfRun ? runEndRadius : runMidRadius`
   *
   * The two raw `px` values are passed in by the caller (which has access
   * to the `responsive` scale) so this helper stays free of UI concerns.
   * Callers typically pass `responsive.borderRadius.lg` (Flutter 14.0 →
   * project `lg`) and `responsive.borderRadius.sm` (Flutter 4.0 → `sm`).
   *
   * Cited from `chat_screen.dart:772-781` (own) / `759-770` (peer).
   */
  static getBubbleCornerRadii({
    isOwn,
    isLastOfRun,
    runMidRadius,
    runEndRadius,
  }: BubbleCornerRadiiInput): BubbleCornerRadii {
    if (isOwn) {
      return {
        topLeft: runMidRadius,
        topRight: runMidRadius,
        bottomLeft: runMidRadius,
        bottomRight: isLastOfRun ? runEndRadius : runMidRadius,
      };
    }
    return {
      topLeft: runMidRadius,
      topRight: runMidRadius,
      bottomLeft: isLastOfRun ? runEndRadius : runMidRadius,
      bottomRight: runMidRadius,
    };
  }
}
